import React, { useEffect, useRef, useState } from 'react';
import { tikzAPI } from '../../services/api';
import { namespaceSvgIds, sizeTikzSvg } from './tikzSvg';

interface MathRendererProps {
  latex: string;
  className?: string;
  onRenderComplete?: () => void;
}

interface ContentBlock {
  type: 'tikz' | 'math' | 'text';
  content: string;
}

const escapeComparatorsInMath = (content: string): string => {
  let result = '';
  let inMath = false;

  for (let i = 0; i < content.length; i += 1) {
    const ch = content[i];
    if (ch === '$') {
      const prev = i > 0 ? content[i - 1] : '';
      if (prev !== '\\') {
        const next = i + 1 < content.length ? content[i + 1] : '';
        if (next === '$') {
          inMath = !inMath;
          result += '$$';
          i += 1;
          continue;
        }
        inMath = !inMath;
      }
      result += ch;
      continue;
    }

    if (inMath && (ch === '<' || ch === '>')) {
      result += ch === '<' ? '&lt;' : '&gt;';
      continue;
    }

    result += ch;
  }

  return result;
};

const MathRenderer: React.FC<MathRendererProps> = ({ latex, className = '', onRenderComplete }) => {
  const mathRef = useRef<HTMLDivElement>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [isRendering, setIsRendering] = useState(true);

  const extractContentBlocks = (content: string): ContentBlock[] => {
    const blocks: ContentBlock[] = [];
    let currentIndex = 0;

    // Find TikZ picture blocks
    const tikzRegex = /\\begin\{tikzpicture\}[\s\S]*?\\end\{tikzpicture\}/g;
    let match;

    while ((match = tikzRegex.exec(content)) !== null) {
      // Add any content before this TikZ block
      if (match.index > currentIndex) {
        const beforeContent = content.slice(currentIndex, match.index).trim();
        if (beforeContent) {
          blocks.push({ type: 'math', content: beforeContent });
        }
      }

      // Add the TikZ block
      blocks.push({ type: 'tikz', content: match[0] });
      currentIndex = match.index + match[0].length;
    }

    // Add any remaining content after the last TikZ block
    if (currentIndex < content.length) {
      const remainingContent = content.slice(currentIndex).trim();
      if (remainingContent) {
        blocks.push({ type: 'math', content: remainingContent });
      }
    }

    // If no TikZ blocks found, treat entire content as math
    if (blocks.length === 0) {
      blocks.push({ type: 'math', content: content });
    }

    return blocks;
  };


  const triggerTikZProcessing = async (container: HTMLElement): Promise<boolean> => {
    try {
      const scripts = container.querySelectorAll('script[type="text/tikz"]');

      if (scripts.length === 0) {
        return false;
      }

      let processedCount = 0;
      const totalScripts = scripts.length;
      let hasSuccess = false;

      return new Promise((resolve) => {
        scripts.forEach(async (script, index) => {
          const tikzCode = script.textContent || '';
          if (!tikzCode.trim()) {
            processedCount++;
            if (processedCount === totalScripts) resolve(hasSuccess);
            return;
          }

          try {
            // Send TikZ code to server-side rendering API using authenticated service
            const result = await tikzAPI.renderTikz(tikzCode);

            if (result.success && result.svg_content) {
              // Create wrapper div and insert SVG
              const svgWrapper = document.createElement('div');
              svgWrapper.className = 'tikz-svg-wrapper';
              svgWrapper.innerHTML = namespaceSvgIds(result.svg_content);
              const svgEl = svgWrapper.querySelector('svg');
              if (svgEl) sizeTikzSvg(svgEl);

              // Replace the script element with the SVG
              if (script.parentNode) {
                script.parentNode.replaceChild(svgWrapper, script);
              } else {
                container.appendChild(svgWrapper);
              }

              hasSuccess = true;
            } else {
              console.error(`❌ Server-side TikZ rendering failed for script ${index + 1}:`, result.error_message);

              // Show fallback error message
              const errorDiv = document.createElement('div');
              errorDiv.className = 'tikz-error';
              errorDiv.style.cssText = 'background: #fffbeb; border: 1px solid #fcd34d; padding: 10px; margin: 10px 0; border-radius: 4px; color: #92400e;';
              const title = document.createElement('strong');
              title.textContent = 'Diagram unavailable';
              const detail = document.createElement('small');
              detail.textContent = 'This diagram could not be rendered.';
              errorDiv.append(title, document.createElement('br'), detail);

              if (script.parentNode) {
                script.parentNode.replaceChild(errorDiv, script);
              } else {
                container.appendChild(errorDiv);
              }
            }
          } catch (error) {
            console.error(`❌ Error sending TikZ script ${index + 1} to server:`, error);

            // Show network error fallback
            const errorDiv = document.createElement('div');
            errorDiv.className = 'tikz-network-error';
            errorDiv.style.cssText = 'background: #fffbeb; border: 1px solid #fcd34d; padding: 10px; margin: 10px 0; border-radius: 4px; color: #92400e;';
            const title = document.createElement('strong');
            title.textContent = 'Diagram unavailable';
            const detail = document.createElement('small');
            detail.textContent = 'The rendering service could not be reached.';
            errorDiv.append(title, document.createElement('br'), detail);

            if (script.parentNode) {
              script.parentNode.replaceChild(errorDiv, script);
            } else {
              container.appendChild(errorDiv);
            }
          }

          processedCount++;
          if (processedCount === totalScripts) {
            resolve(hasSuccess);
          }
        });
      });

    } catch (error) {
      console.error('❌ Server-side TikZ processing setup failed:', error);
      return false;
    }
  };

  useEffect(() => {
    if (!mathRef.current) {
      return;
    }
    if (!latex) {
      setIsRendering(false);
      onRenderComplete?.();
      return;
    }

    let cancelled = false;

    const renderMathContent = async () => {
      if (cancelled || !mathRef.current) return;
      try {
        setRenderError(null);
        setIsRendering(true);

        // Clear the container
        mathRef.current!.innerHTML = '';

        // Extract content blocks
        const contentBlocks = extractContentBlocks(latex);
        const hasTikZ = contentBlocks.some(block => block.type === 'tikz');

        if (hasTikZ) {
          // Process each content block
          for (const block of contentBlocks) {
            if (cancelled || !mathRef.current) return;
            if (block.type === 'tikz') {
              // Create script element for TikZ content
              const script = document.createElement('script');
              script.type = 'text/tikz';
              script.textContent = block.content;
              script.setAttribute('data-processed', 'false');
              mathRef.current!.appendChild(script);

            } else if (block.type === 'math' && block.content.trim()) {
              // Create div for regular math content
              const mathDiv = document.createElement('div');
              mathDiv.innerHTML = escapeComparatorsInMath(block.content);
              mathRef.current!.appendChild(mathDiv);

              // Process with MathJax
              if ((window as any).MathJax && (window as any).MathJax.typesetPromise) {
                try {
                  await (window as any).MathJax.typesetPromise([mathDiv]);
                } catch (mathJaxError) {
                  console.error('❌ MathJax error:', mathJaxError);
                }
              }
            }
          }

          if (cancelled || !mathRef.current) return;

          // Attempt server-side TikZ processing
          try {
            const processed = await triggerTikZProcessing(mathRef.current!);

            // Clear any previous error if processing was successful
            if (processed) {
              setRenderError(null);
            }
          } catch (tikzError) {
            console.warn('⚠️ Server-side TikZ processing encountered issues:', tikzError);
            setRenderError('TikZ rendering service encountered an error. Please try refreshing the page.');
          } finally {
            setIsRendering(false);
            onRenderComplete?.();
          }

        } else {
          // Standard MathJax processing for non-TikZ content
          mathRef.current!.innerHTML = escapeComparatorsInMath(latex);

          if ((window as any).MathJax && (window as any).MathJax.typesetPromise) {
            try {
              await (window as any).MathJax.typesetPromise([mathRef.current]);
            } catch (mathJaxError) {
              console.error('❌ MathJax error:', mathJaxError);
              setRenderError('Math rendering failed');
            } finally {
              setIsRendering(false);
              onRenderComplete?.();
            }
          } else {
            setIsRendering(false);
            onRenderComplete?.();
          }
        }
      } catch (error) {
        if (cancelled || !mathRef.current) return;
        console.error('❌ Math rendering error:', error);
        setRenderError('Content rendering failed');
        // Fallback to raw content
        mathRef.current!.innerHTML = '';
        const fallback = document.createElement('pre');
        fallback.style.cssText = 'background: #f5f5f5; padding: 10px; border-radius: 4px; overflow-x: auto; color: #333; font-family: monospace; white-space: pre-wrap;';
        fallback.textContent = latex;
        mathRef.current!.appendChild(fallback);
        setIsRendering(false);
        onRenderComplete?.();
      }
    };

    // Small delay to ensure DOM is ready and libraries are loaded
    const timer = setTimeout(renderMathContent, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [latex, onRenderComplete]);

  return (
    <div className={`math-renderer ${className}`}>
      {renderError && (
        <div className="text-sm text-red-600 dark:text-red-400 mb-2 p-2 bg-red-50 dark:bg-red-900/20 rounded">
          ⚠️ {renderError}
        </div>
      )}
      {isRendering && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-subject-500"></div>
        </div>
      )}
      <div
        ref={mathRef}
        className={`math-content ${isRendering ? 'opacity-0' : 'opacity-100'}`}
        style={{
          minHeight: '20px',
          lineHeight: '1.6',
          transition: 'opacity 0.3s ease-in-out',
          // Ensure TikZ graphics are visible and properly styled
        }}
      >
        {latex}
      </div>
    </div>
  );
};

export default MathRenderer;
